import Link from "next/link";
import { type ReactNode, useContext } from "react";
import {
  LuCircleMinus as LuMinusCircle,
  LuCirclePlus as LuPlusCircle,
  LuTriangleAlert as LuAlertTriangle,
  LuX,
} from "react-icons/lu";
import { MdOutlineOpenInNew } from "react-icons/md";
import { useStartViewTransitionState } from "use-start-view-transition";

import { EditorContextProvider } from "@/components/app/editor-v3/editor-context";
import { CommentEditor } from "@/components/app/editor-v3/editors/comment-editor";
import { toPostRequestParams } from "@/components/app/editor-v3/transforms/to-post-request-params";
import EmbedPlayer from "@/components/app/post/embed-player";
import { useCommentsContext } from "@/components/app/post/post-comments/comments-context";
import { ContentBlurOverlay } from "@/components/shared/content-blur-overlay";
import { DelegatedLink } from "@/components/shared/delegated-link";
import DesoAvatar from "@/components/shared/deso-avatar";
import { InfoTooltip } from "@/components/shared/info-tooltip";
import { PostAudioPlayer } from "@/components/shared/media-player/post-audio-player";
import { PostImageSection } from "@/components/shared/post-image/post-image-section";
import { StopPropagationWrapper } from "@/components/shared/stop-propagation-wrapper";
import { UserDisplayNameWithStatus } from "@/components/shared/user-display-name-with-status";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownDrawerItem } from "@/components/ui/dropdown-drawer";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { useToast } from "@/components/ui/use-toast";
import { useFeedContext } from "@/contexts/feed-context";
import { useLayout } from "@/contexts/layout-context";
import { usePostCacheContext } from "@/contexts/post-cache-context";
import { usePostContext } from "@/contexts/post-context";
import { UserContext } from "@/contexts/user-context";
import type { Post } from "@/graphql/codegen/graphql";
import { useMobile } from "@/hooks/use-mobile";
import { useSubmitPost } from "@/hooks/use-submit-post";
import { useTrackViews } from "@/hooks/use-track-views";
import { useGetCurrentUserPostStats } from "@/hooks-api/use-get-current-user-post-stats";
import { DEFAULT_POST_COMMENT_FORMAT, FocusDateParser } from "@/utils/date";
import { pluralize } from "@/utils/helpers";
import { cn } from "@/utils/lib";
import { getUserName } from "@/utils/usernames";

import { PostActionsDropdown } from "../post-actions/post-actions-dropdown";
import { PostEmojiActivity } from "../post-activity/post-emoji-activity";
import { PostBody } from "../post-body";
import { PostUpvote } from "../post-upvote";
import { PostVideoSection } from "../post-video-section";
import { PostCommentActions } from "./post-comment-actions";

const INTERSECTION_ACTION_DELAY = 250;

type CommentContentProps = {
  hideEmoji?: boolean;
  hideActions?: boolean;
  childrenCollapsed?: boolean;
  selfCollapsed?: boolean;
  hoveredLine?: { postHash: string; lineLevel: number } | null;
  commentsCount?: number;
  children?: ReactNode;
  isLastItem?: boolean;
  nestingLevel?: number;
  hasChildren?: boolean;
  postCreatorPublicKey: string;
  parentMeta?: Record<
    number,
    { isCurrentChildLastItem: boolean; total: number; postHash?: string }
  >;
  showMoreCommentsButton?: boolean;
  onToggleChildrenClick?: () => void;
  onToggleSelfClick?: () => void;
  onLineClick?: (postHash?: string) => void;
  onLineEnter?: (postHash: string, lineLevel: number) => void;
  onLineLeave?: (postHash: string, lineLevel: number) => void;
  clickable?: boolean;
};

const nestedMarginClassNameByLevel: Record<number, string> = {
  1: "ml-0",
  2: "ml-[20px] md:ml-[45px]",
  3: "ml-[40px] md:ml-[90px]",
  4: "ml-[60px] md:ml-[135px]",
  5: "ml-[80px] md:ml-[180px]",
  6: "ml-[100px] md:ml-[225px]",
};

// + nestedMarginClassNameByLevel on each level
const nestedLineClassNameByIndex: Record<number, string> = {
  1: "left-[-13px] md:left-[-27px]",
  2: "left-[-33px] md:left-[-72px]",
  3: "left-[-53px] md:left-[-117px]",
  4: "left-[-73px] md:left-[-162px]",
  5: "left-[-93px] md:left-[-207px]",
};

const ENABLE_MARKED_AS_SPAM_FEATURE = true;

export const CommentContent = ({
  hideEmoji = false,
  hideActions = false,
  childrenCollapsed = false,
  selfCollapsed = false,
  hoveredLine = null,
  isLastItem = false,
  hasChildren = false,
  clickable = false,
  nestingLevel = 1,
  postCreatorPublicKey,
  children,
  parentMeta,
  commentsCount = 0,
  showMoreCommentsButton,
  onToggleChildrenClick,
  onToggleSelfClick,
  onLineClick,
  onLineEnter,
  onLineLeave,
}: CommentContentProps) => {
  const {
    body,
    timestamp,
    postHash,
    publicKey,
    post,
    post: { extraData },
    poster,
    imageUrls,
    username,
    mediaMeta,
    videoUrls,
    postCachePrefix,
    updatePost,
  } = usePostContext();
  const { postCacheSignal, nsfwRevealed, showCommentEditor, postCacheKey } =
    usePostCacheContext(postHash, postCachePrefix);
  const submitComment = useSubmitPost();
  const { toast } = useToast();
  const { isMobile } = useMobile();
  const { ios } = useLayout();

  const audioUrls = extraData?.AudioURLs
    ? JSON.parse(extraData?.AudioURLs)
    : undefined;

  const { tempFilters } = useFeedContext();
  const { onCommentCreated } = useCommentsContext();

  const { user, isUserBlocked, currentUserPublicKey } = useContext(UserContext);

  const isPosterBlacklisted = poster?.isBlacklisted;
  const isPosterCurrentUser = poster?.publicKey === currentUserPublicKey;
  const isPosterNSFW = (poster?.isNSFW?.totalCount ?? 0) > 0;
  const isPostNSFW = (post?.nsfwCount?.totalCount ?? 0) > 0;

  const isBlocked = isUserBlocked(poster?.publicKey);

  // We never hide the comment if the poster is the current user
  const [_markedAsSpam, setMarkedAsSpam] = useStartViewTransitionState(
    isPosterBlacklisted && !isPosterCurrentUser,
  );
  const markedAsSpam =
    ENABLE_MARKED_AS_SPAM_FEATURE && _markedAsSpam && tempFilters.useModeration;

  const showCommentImageSection = (imageUrls?.length ?? 0) > 0;
  const showCommentVideoSection = (videoUrls?.length ?? 0) > 0;
  const isMarkdown = extraData.isMarkdown === "true";
  const { repostCount, quoteCount, viewCount } =
    useGetCurrentUserPostStats(post);
  const { ref } = useTrackViews({
    postHash,
    delay: INTERSECTION_ACTION_DELAY,
    postCachePrefix,
  });
  const parentPostHash = parentMeta?.[nestingLevel - 1]?.postHash;

  const guides = [];

  for (let level = nestingLevel, i = 1; level > 1; level--, i++) {
    // No need for own guide if it's the last post
    if (level === nestingLevel && isLastItem) {
      continue;
    }

    const lineLevel = level - 1;
    const currentParentMeta = parentMeta?.[lineLevel];
    const currentParentPostHash = currentParentMeta?.postHash;

    if (currentParentMeta?.isCurrentChildLastItem || !currentParentPostHash) {
      continue;
    }

    guides.push(
      <div
        key={i}
        className={cn(
          `
            absolute top-[-6px] z-1 h-[calc(100%+24px)] cursor-pointer border-l
            border-solid border-border
            before:absolute before:top-0 before:right-full before:h-full
            before:w-[10px]
            after:absolute after:top-0 after:left-full after:h-full
            after:w-[10px]
          `,
          nestedLineClassNameByIndex[i],
          {
            "border-muted-foreground":
              hoveredLine?.lineLevel === lineLevel &&
              hoveredLine?.postHash === currentParentPostHash,
          },
        )}
        onMouseEnter={() => onLineEnter?.(currentParentPostHash, lineLevel)}
        onMouseLeave={() => onLineLeave?.(currentParentPostHash, lineLevel)}
        onClick={() => onLineClick?.(currentParentMeta?.postHash)}
        data-guide-for-level={lineLevel}
        data-guide-for-post-hash={currentParentPostHash}
        data-nesting-level={nestingLevel}
        role="button"
      />,
    );
  }

  if (
    !poster ||
    isBlocked ||
    (tempFilters.hideNSFW === "hide" && (isPosterNSFW || isPostNSFW))
  )
    return null;

  return (
    <>
      <div className="relative flex items-stretch gap-2" ref={ref}>
        <div
          className={cn(
            "relative flex-col items-center gap-2 pb-2 text-muted",
            nestedMarginClassNameByLevel[nestingLevel],
          )}
        >
          {/* Line connecting comments having children */}
          {!selfCollapsed && hasChildren && (
            <div
              className={cn(
                `
                  absolute top-[calc(100%-10px)] left-[7px] z-1 h-[20px]
                  cursor-pointer border-l border-solid border-border
                  before:absolute before:top-0 before:right-full before:h-full
                  before:w-[10px]
                  after:absolute after:top-0 after:left-full after:h-full
                  after:w-[10px]
                  md:top-[110px] md:left-1/2 md:h-[calc(100%-100px)]
                `,
                {
                  "h-0 md:h-[calc(100%-144px)]": childrenCollapsed,
                  "hidden md:block": !hasChildren,
                  "border-muted-foreground":
                    hoveredLine?.lineLevel === nestingLevel &&
                    hoveredLine?.postHash === postHash,
                },
              )}
              onMouseEnter={() => onLineEnter?.(postHash, nestingLevel)}
              onMouseLeave={() => onLineLeave?.(postHash, nestingLevel)}
              onClick={() => onLineClick?.(postHash)}
              role="button"
            />
          )}
          {/* Line connecting comments to parent (guides) */}
          {guides}
          {/* Curve */}
          {nestingLevel > 1 && parentPostHash && (
            <div
              className={cn(
                `
                  unforce-radius absolute top-[-14px] z-1 block h-[33px]
                  w-[10px] cursor-pointer rounded-bl-[min(5px,var(--radius))]
                  border-b border-l border-solid border-border
                  md:left-1/2 md:w-[20px] md:rounded-bl-lg
                `,
                nestedLineClassNameByIndex[1],
                {
                  "border-muted-foreground":
                    hoveredLine?.lineLevel === nestingLevel - 1 &&
                    hoveredLine?.postHash ===
                      parentMeta?.[nestingLevel - 1]?.postHash,
                },
              )}
              onClick={() => onLineClick?.(parentPostHash)}
              onMouseEnter={() =>
                onLineEnter?.(parentPostHash, nestingLevel - 1)
              }
              onMouseLeave={() =>
                onLineLeave?.(parentPostHash, nestingLevel - 1)
              }
              data-curve-for-level={nestingLevel - 1}
              data-curve-for-post-hash={parentPostHash}
              role="button"
            />
          )}
          {!selfCollapsed && hasChildren && !showCommentEditor && (
            <div
              className="
                hidden
                md:contents
              "
            >
              <InfoTooltip
                text={
                  <span className="text-xs text-muted">
                    {childrenCollapsed ? "Show replies" : "Hide replies"}
                  </span>
                }
              >
                <button
                  className="
                    absolute bottom-0 left-1/2 z-2
                    translate-x-[calc(-50%+0.6px)] cursor-pointer p-2 text-lg
                    text-muted
                    hover:text-muted-foreground
                  "
                  onClick={onToggleChildrenClick}
                >
                  <div
                    className="
                      border-4 border-solid border-background bg-background
                    "
                  >
                    {childrenCollapsed ? <LuPlusCircle /> : <LuMinusCircle />}
                  </div>
                </button>
              </InfoTooltip>
            </div>
          )}
          {!selfCollapsed && (
            <>
              <DesoAvatar {...{ account: poster, size: 36 }} />
              <div
                className="
                  relative mx-auto mt-2 hidden h-[64px] w-[30px]
                  md:block
                "
              >
                <div className="absolute top-0 left-1/2 -translate-x-1/2">
                  <PostUpvote
                    {...{
                      postHash,
                    }}
                    compact
                  />
                </div>
              </div>
            </>
          )}

          {selfCollapsed && (
            <button
              className="
                relative top-[2px] cursor-pointer p-[5px] text-lg text-muted
                hover:text-muted-foreground
              "
              onClick={() => onToggleSelfClick?.()}
            >
              <div
                className="
                  border-4 border-solid border-background bg-background
                "
              >
                <LuPlusCircle />
              </div>
            </button>
          )}
        </div>
        <div className="flex w-full min-w-0 flex-col items-start">
          <div
            className="flex w-full cursor-pointer items-center gap-2"
            onClick={() => {
              onToggleSelfClick?.();
            }}
            role="button"
          >
            <div className="flex flex-col items-start gap-0">
              <div
                className="
                  flex items-center gap-2 text-sm font-medium
                  text-muted-foreground
                "
              >
                <StopPropagationWrapper className="flex flex-row items-center">
                  <UserDisplayNameWithStatus
                    whaleSize={16}
                    {...{
                      username,
                      publicKey,
                      extraData: poster.extraData,
                      isVerified: poster.isVerified,
                    }}
                    className="text-xs"
                  />
                </StopPropagationWrapper>
                {postCreatorPublicKey === poster.publicKey && (
                  <Badge
                    variant="outline"
                    className="border-muted-foreground px-1 text-primary"
                  >
                    <span className="text-[10px]">OP</span>
                  </Badge>
                )}
                {selfCollapsed && commentsCount > 0 && (
                  <span className="text-xs text-muted">
                    [{pluralize(commentsCount, "reply")}]
                  </span>
                )}
              </div>
              <div
                className="
                  flex w-full cursor-pointer flex-row gap-1 text-xs font-normal
                  text-muted
                "
              >
                <span>{getUserName({ account: poster })}</span>
                <span>•</span>
                <time
                  title={FocusDateParser.formatUTCDate(
                    timestamp,
                    DEFAULT_POST_COMMENT_FORMAT,
                  )}
                >
                  {FocusDateParser.formatUTCDateFromNowShort(timestamp, {
                    forcePast: true,
                  })}
                </time>
              </div>
            </div>
            <StopPropagationWrapper className="ml-auto flex items-center gap-2">
              <PostActionsDropdown
                compact
                account={poster}
                extraItems={
                  <DropdownDrawerItem
                    closeOnSelect={false}
                    render={
                      <Link href={`/post/${postHash}`} prefetch={false} replace>
                        <MdOutlineOpenInNew className="mr-2 size-4 text-muted" />
                        <span>Link to Comment</span>
                      </Link>
                    }
                  />
                }
              />
            </StopPropagationWrapper>
          </div>
          {!selfCollapsed && !markedAsSpam && (
            <div
              className="
                relative ml-[-44px] flex w-[calc(100%+44px)] flex-col gap-2
                md:ml-0 md:min-h-[101px] md:w-full
              "
            >
              {post.isHidden ? (
                <div
                  className="
                    my-1 w-full rounded-md border border-muted bg-muted/20 p-3
                    text-sm text-muted-foreground
                  "
                >
                  This post was removed by the author.
                </div>
              ) : (
                <>
                  <DelegatedLink
                    href={`/post/${postHash}`}
                    prefetch={false}
                    enabled={clickable}
                  >
                    <PostBody
                      {...{ body, postHash, isMarkdown }}
                      className={cn(
                        `
                          !md:gap-2
                          my-1 w-full gap-2! text-sm/6 whitespace-pre-line
                        `,
                        {
                          "cursor-pointer": clickable,
                        },
                      )}
                    />
                  </DelegatedLink>

                  {audioUrls?.map((url: string) => {
                    return (
                      <PostAudioPlayer
                        key={url}
                        src={url}
                        mediaMeta={mediaMeta}
                        postHash={postHash}
                      />
                    );
                  })}

                  <EmbedPlayer
                    url={[extraData?.EmbedVideoURL, body]}
                    disabledURLs={extraData?.DisabledEmbedURLs}
                  />

                  {showCommentImageSection && imageUrls && (
                    <PostImageSection
                      mediaMeta={mediaMeta}
                      urls={imageUrls.filter(Boolean)}
                      postHash={postHash}
                    />
                  )}
                  {showCommentVideoSection && videoUrls && (
                    <PostVideoSection
                      videoUrls={videoUrls.filter(Boolean)}
                      postHash={postHash}
                    />
                  )}
                </>
              )}
              <div className="relative mt-auto w-full">
                {/* We need this placeholder on mobile to avoid overlapping upvote buttons */}
                {!hideEmoji && (
                  <div
                    className="
                      flex min-h-[40px] items-center gap-2
                      md:min-h-0
                    "
                  >
                    <PostEmojiActivity />
                    <div
                      className="
                        ml-auto
                        md:hidden
                      "
                    >
                      <PostUpvote compact horizontal />
                    </div>
                  </div>
                )}
                {!hideActions && poster.publicKey && (
                  <div className="flex">
                    {hasChildren && (
                      <button
                        className="
                          translate-x-[-7.5px] p-2 text-sm text-muted
                          hover:text-muted-foreground
                          md:hidden
                        "
                        onClick={onToggleChildrenClick}
                      >
                        {childrenCollapsed ? (
                          <LuPlusCircle />
                        ) : (
                          <LuMinusCircle />
                        )}
                      </button>
                    )}
                    <PostCommentActions
                      {...{
                        repostsCount: repostCount!,
                        quotedRepostsCount: quoteCount!,
                        postCreatorPublicKey,
                        creatorPublicKey: poster.publicKey,
                        viewsCount: viewCount!,
                      }}
                    />
                  </div>
                )}
                {showCommentEditor && (
                  <EditorContextProvider
                    autoFocus
                    enableFileDrop
                    onSubmit={async (editorContext) => {
                      const { setEditorContext, reset, exposed } =
                        editorContext;
                      const setModalOpen = exposed?.setModalOpen;

                      if (typeof setModalOpen === "function") {
                        setModalOpen(false);
                      }

                      setEditorContext({
                        submitting: true,
                      });
                      const requestData = await toPostRequestParams({
                        editorContext,
                        user: user!,
                        initialData: {
                          UpdaterPublicKeyBase58Check: currentUserPublicKey,
                          ParentStakeID: postHash,
                        },
                      });
                      try {
                        const comment = await submitComment(requestData, {
                          toleratePostHydrationFailure: true,
                        });

                        if (comment) {
                          updatePost?.(post.postHash!, {
                            ...post,
                            postStats: {
                              ...post.postStats,
                              replyCount: (post.postStats?.replyCount || 0) + 1,
                            },
                          });
                          onCommentCreated(comment as Post);
                        }
                        reset();
                        postCacheSignal.value = {
                          ...postCacheSignal.value,
                          showCommentEditor: false,
                        };
                      } catch (error) {
                        toast({
                          title: "Error submitting comment",
                          description: `An error happened when submitting your comment`,
                          details: createToastErrorDetails(error),
                          variant: "destructive",
                        });
                      } finally {
                        setEditorContext({
                          submitting: false,
                        });
                      }
                    }}
                  >
                    <div
                      data-inline-reply-composer={postCacheKey}
                      style={
                        isMobile
                          ? ios
                            ? {
                                right: 12,
                                bottom:
                                  "calc(var(--visual-keyboard-inset-bottom, 0px) + 8px)",
                                left: 12,
                                border: "1px solid var(--border)",
                                borderRadius: "var(--radius)",
                                boxShadow:
                                  "0 0 0 1px rgb(255 255 255 / 8%), 0 -14px 34px rgb(0 0 0 / 62%), 0 16px 34px rgb(0 0 0 / 78%), 0 38px 90px rgb(0 0 0 / 92%)",
                              }
                            : {
                                bottom:
                                  "var(--visual-keyboard-inset-bottom, 0px)",
                              }
                          : undefined
                      }
                      className={cn({
                        "fixed inset-x-0 z-60 border-t border-border bg-background p-4":
                          isMobile,
                        "mb-2 border border-border p-2": !isMobile,
                      })}
                    >
                      {isMobile && (
                        <Button
                          className={cn(
                            `
                              absolute -top-9 right-2 size-auto bg-background
                              p-1 hover:bg-background
                            `,
                            {
                              "shadow-[0_10px_28px_rgb(0_0_0/65%)]": ios,
                            },
                          )}
                          size="icon"
                          variant="outline"
                          onClick={() =>
                            (postCacheSignal.value = {
                              ...postCacheSignal.value,
                              showCommentEditor: false,
                            })
                          }
                        >
                          <LuX className="text-lg" />
                        </Button>
                      )}
                      <CommentEditor to={username} />
                    </div>
                  </EditorContextProvider>
                )}
              </div>
              {tempFilters.hideNSFW === "blur" &&
                (isPosterNSFW || isPostNSFW) &&
                !nsfwRevealed && (
                  <ContentBlurOverlay
                    buttonText="Show Post"
                    onReveal={() => {
                      postCacheSignal.value = {
                        ...postCacheSignal.value,
                        nsfwRevealed: true,
                      };
                    }}
                  >
                    {!isPosterNSFW && (
                      <p className="flex flex-row items-center gap-2">
                        <LuAlertTriangle className="inline-block text-lg" />
                        This post has been tagged as NSFW.
                      </p>
                    )}
                    {isPosterNSFW && (
                      <p className="flex flex-row items-center gap-2">
                        <LuAlertTriangle className="inline-block text-lg" />
                        This user has been tagged as NSFW.
                      </p>
                    )}
                  </ContentBlurOverlay>
                )}
            </div>
          )}
          {markedAsSpam && (
            <Alert variant="warning" className="my-3">
              <p className="mb-2">
                This reply has been marked as spam by the community. If you'd
                like to see all content by default, simply disable content
                censorship on your For You page.
              </p>
              <Button
                variant="destructive"
                size="xs"
                onClick={() => setMarkedAsSpam(false)}
              >
                View anyway
              </Button>
            </Alert>
          )}
          {showMoreCommentsButton && (
            <Link href={`/post/${postHash}`} className="my-1">
              <Button
                variant="ghost"
                className="flex items-center gap-2 p-0"
                size="xs"
              >
                <LuPlusCircle />
                <span className="underline underline-offset-4">
                  Show more comments
                </span>
              </Button>
            </Link>
          )}
        </div>
      </div>
      {!childrenCollapsed && children}
    </>
  );
};
